import * as React from "react";
import { getDefaultWorkloadInput } from "../draft/utils/utils";
import { formInitialize, formUninitialize, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { v4 as uuidv4 } from "uuid";
import { WorkloadCreateContent } from "./content";
import { getValidator } from "../draft/utils/validator";
import { WorkloadInput } from "../../../schema/types/workload/input";
import { store } from "../../../store";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { inputToWorkload } from "../draft/utils/inputToWorkload";
import { parentLink, request, resourceLink } from "../../../services/cpln";
import { useBlocker } from "react-router-dom";
import { sentryLog } from "../../../tools/sentry";
import { notification } from "antd";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { CpPreviewModal } from "../draft/previewModal";
import { CpDebugModal } from "../draft/debugModal";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { workloadApi } from "../../../store/api/workloadApi";

export const WorkloadCreateForm: React.FC = () => {
  const dispatch = useAppDispatch();

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = getDefaultWorkloadInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // prompt
  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/workload/-create`) &&
      !nextLocation.pathname.includes(`/workload/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  // validation/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: WorkloadInput) {
    try {
      const org = store.getState().context.org;
      const gvc = parseLink(input.gvc.link, { useInputCtx: true }).name;
      const ctx = { org: org, gvc: gvc };
      let body = inputToWorkload(input);
      const method = "post";
      const url = parentLink("workload", ctx);

      await request({
        method: method,
        url: url,
        body: body,
      });

      const itemLink = resourceLink("workload", body.name, ctx);
      dispatch(workloadApi.util.invalidateTags([{ type: "Workload", id: itemLink }]));
      ignoreBlockerRef.current = true;
      notification.success({
        message: "Success",
        description: "Workload is created",
      });
      dispatch(setRedirect(`/console${itemLink}`));
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <WorkloadCreateContent ignoreBlockerRef={ignoreBlockerRef} validator={validator} />
      <CpFormValidator validationFn={validator} />
      <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
      <CpPreviewModal inputToItemFn={inputToWorkload} kind="workload" namePath="name" title="Workload" />
      <CpDebugModal inputToItemFn={inputToWorkload} kind="workload" namePath="name" title="Workload" />
    </CpFormContext.Provider>
  );
};
